import React from 'react';
import { cn } from '@/shared/utils';
import { TrendingUp, TrendingDown } from 'lucide-react';

export const MobileStatsCard = ({ 
  value, 
  label, 
  trend, 
  icon: Icon,
  color = 'default',
  className 
}) => {
  const colorClasses = {
    default: '',
    success: 'border-l-4 border-l-[var(--mobile-success)]',
    warning: 'border-l-4 border-l-[var(--mobile-warning)]',
    error: 'border-l-4 border-l-[var(--mobile-error)]',
    earnings: 'border-l-4 border-l-[var(--mobile-earnings)]',
  };

  return (
    <div className={cn('mobile-stats-card', colorClasses[color], className)}>
      {Icon && (
        <Icon 
          className="w-5 h-5 mb-2 mx-auto" 
          style={{ color: `var(--mobile-${color === 'default' ? 'text-secondary' : color})` }}
        />
      )}
      <div className="mobile-stats-value">{value}</div>
      <div className="mobile-stats-label">{label}</div>
      {trend && (
        <div className={cn(
          'mobile-stats-trend mobile-flex mobile-items-center mobile-justify-center mobile-gap-1',
          trend.direction === 'up' ? 'mobile-stats-trend-up' : 'mobile-stats-trend-down'
        )}>
          {trend.direction === 'up' ? (
            <TrendingUp className="w-3 h-3" />
          ) : (
            <TrendingDown className="w-3 h-3" />
          )}
          <span>{trend.value}</span>
        </div>
      )}
    </div>
  );
};

export const MobileButton = ({ 
  children, 
  variant = 'primary', 
  size = 'default',
  fullWidth = false,
  icon: Icon,
  loading = false,
  disabled = false,
  className,
  onClick,
  ...props 
}) => {
  const variantClasses = {
    primary: 'mobile-btn-primary',
    secondary: 'mobile-btn-secondary',
    ghost: 'mobile-btn-ghost',
    success: 'mobile-btn-success',
    pill: 'mobile-btn-primary mobile-btn-pill',
  };

  const sizeClasses = {
    small: 'text-sm py-2 px-3',
    default: '',
    large: 'mobile-btn-large',
  };

  return (
    <button
      className={cn(
        'mobile-btn',
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'mobile-btn-full',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {loading ? (
        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full mobile-animate-spin" />
      ) : (
        <>
          {Icon && <Icon className="w-5 h-5" />}
          {children}
        </>
      )}
    </button>
  );
};

export const MobileCard = ({ 
  children, 
  elevated = false, 
  interactive = false,
  className,
  onClick,
  ...props 
}) => {
  return (
    <div
      className={cn(
        'mobile-card',
        elevated && 'mobile-card-elevated',
        interactive && 'mobile-card-interactive',
        className
      )}
      onClick={onClick}
      {...props}
    >
      {children}
    </div>
  );
};

export const MobileBadge = ({ children, variant = 'default', className }) => {
  const variantClasses = {
    default: '',
    success: 'mobile-badge-success',
    warning: 'mobile-badge-warning',
    info: 'mobile-badge-info',
  };

  return (
    <span className={cn('mobile-badge', variantClasses[variant], className)}>
      {children}
    </span>
  );
};

export const MobileProgress = ({ value, max = 100, className }) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  return (
    <div className={cn('mobile-progress-bar', className)}>
      <div 
        className="mobile-progress-fill" 
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

export const MobileDivider = ({ className }) => (
  <div 
    className={cn('h-px w-full', className)} 
    style={{ background: 'var(--mobile-divider)' }}
  />
);

export const MobileCountdown = ({ seconds, total, className }) => {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const progress = (seconds / total) * circumference;

  return (
    <div className={cn('mobile-countdown', className)}>
      <svg className="mobile-countdown-ring" width="80" height="80">
        <circle className="bg" cx="40" cy="40" r={radius} />
        <circle 
          className="progress" 
          cx="40" 
          cy="40" 
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
        />
      </svg>
      <span className="mobile-countdown-value">{seconds}s</span>
    </div>
  );
};
